///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface VolcanoProperties extends DatasetFeature {
	number: number                          // Smithsonian volcano number (unique ID)
	name: string                            // Volcano name
	type: string                            // Volcano type (Stratovolcano, Shield, etc.)
	country: string                         // Country
	region: string                          // Major region
	subregion: string                       // Subregion
	lon: number                             // Longitude
	lat: number                             // Latitude
	elevation_m: number | null              // Elevation in meters
	last_eruption: string | null            // Last eruption (year or description)
	epoch: string | null                    // Geological epoch (Holocene, Pleistocene)

	// Optional geological characteristics
	landform?: string | null
	tectonic_setting?: string | null
	evidence_category?: string | null
	rock_type?: string | null
	geological_summary?: string | null

	// Optional photo metadata
	photo_link?: string | null
	photo_caption?: string | null
	photo_credit?: string | null

	updated_at?: string | null
}


const cBatchSize = 500

export class VolcanoesTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists volcanoes (
					id text primary key,
					number integer not null unique,
					name text not null,
					type text not null,
					country text not null,
					region text not null,
					subregion text not null,
					lon real not null,
					lat real not null,
					elevation_m integer,
					last_eruption text,
					epoch text,
					landform text,
					tectonic_setting text,
					evidence_category text,
					rock_type text,
					geological_summary text,
					photo_link text,
					photo_caption text,
					photo_credit text,
					updated_at text,
					geom blob
				)`
			},
			{ sql: "create index if not exists idx_volcanoes_name on volcanoes(name collate nocase)" },
			{ sql: "create index if not exists idx_volcanoes_type on volcanoes(type)" },
			{ sql: "create index if not exists idx_volcanoes_country on volcanoes(country)" },
			{ sql: "create index if not exists idx_volcanoes_region on volcanoes(region)" },
			{ sql: "create index if not exists idx_volcanoes_epoch on volcanoes(epoch)" },
			{ sql: "create index if not exists idx_volcanoes_elevation on volcanoes(elevation_m desc)" },

			{
				sql: `create virtual table if not exists volcanoes_idx using rtree(
					id,
					min_x, max_x,
					min_y, max_y
				)`
			},
			{
				sql: `create trigger if not exists volcanoes_idx_insert
				after insert on volcanoes
				begin
					insert into volcanoes_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists volcanoes_idx_update
				after update on volcanoes
				begin
					delete from volcanoes_idx where id = old.rowid;
					insert into volcanoes_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists volcanoes_idx_delete
				after delete on volcanoes
				begin
					delete from volcanoes_idx where id = old.rowid;
				end`
			},
		])
	}

	public static async insert(rows: VolcanoProperties[]): Promise<void> {
		const statement = `insert into volcanoes (
				id, number, name, type, country, region, subregion,
				lon, lat, elevation_m, last_eruption, epoch,
				landform, tectonic_setting, evidence_category, rock_type, geological_summary,
				photo_link, photo_caption, photo_credit,
				updated_at,
				geom
			) values (
				?, ?, ?, ?, ?, ?, ?,
				?, ?, ?, ?, ?,
				?, ?, ?, ?, ?,
				?, ?, ?,
				?,
				ST_SetSRID(ST_MakePoint(?, ?, ?), 4326)
			)
			on conflict(id) do update set
				number = excluded.number,
				name = excluded.name,
				type = excluded.type,
				country = excluded.country,
				region = excluded.region,
				subregion = excluded.subregion,
				lon = excluded.lon,
				lat = excluded.lat,
				elevation_m = excluded.elevation_m,
				last_eruption = excluded.last_eruption,
				epoch = excluded.epoch,
				landform = excluded.landform,
				tectonic_setting = excluded.tectonic_setting,
				evidence_category = excluded.evidence_category,
				rock_type = excluded.rock_type,
				geological_summary = excluded.geological_summary,
				photo_link = excluded.photo_link,
				photo_caption = excluded.photo_caption,
				photo_credit = excluded.photo_credit,
				updated_at = excluded.updated_at,
				geom = excluded.geom`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(v => {
				return [
					v.id,
					v.number,
					v.name,
					v.type,
					v.country,
					v.region,
					v.subregion,
					v.lon,
					v.lat,
					v.elevation_m,
					v.last_eruption,
					v.epoch,
					v.landform ?? null,
					v.tectonic_setting ?? null,
					v.evidence_category ?? null,
					v.rock_type ?? null,
					v.geological_summary ?? null,
					v.photo_link ?? null,
					v.photo_caption ?? null,
					v.photo_credit ?? null,
					v.updated_at ?? null,
					v.lon,
					v.lat,
					v.elevation_m ?? 0,
				]
			}))
		}
	}

	public static async getAll(): Promise<VolcanoProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, number, name, type, country, region, subregion,
				lon, lat, elevation_m, last_eruption, epoch,
				landform, tectonic_setting, evidence_category, rock_type, geological_summary,
				photo_link, photo_caption, photo_credit, updated_at
			from volcanoes
			order by name collate nocase`,
			[],
			"low"
		)
		return rows as VolcanoProperties[]
	}

	public static async getByType(type: string): Promise<VolcanoProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, number, name, type, country, region, subregion,
				lon, lat, elevation_m, last_eruption, epoch,
				landform, tectonic_setting, evidence_category, rock_type, geological_summary,
				photo_link, photo_caption, photo_credit, updated_at
			from volcanoes
			where type = ?
			order by name collate nocase`,
			[type],
			"low"
		)
		return rows as VolcanoProperties[]
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from volcanoes`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	public static async getTypes(): Promise<string[]> {
		const rows = await Database.db.exec(
			`select distinct type from volcanoes where type is not null order by type`,
			[],
			"low"
		) as Array<{ type: string }>
		return rows.map(r => r.type)
	}
}


